import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import './PrivacyConsentDocument.css';

function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function toArray(value) {
    return Array.isArray(value) ? value : [];
}

function isPrivacyDocument(value) {
    return isObject(value) && isObject(value.document_info) &&
        Array.isArray(value.privacy_policy_chapters);
}

function normalizeLanguage(language) {
    const normalized = String(language || 'ko').trim().toLowerCase()
        .replace('_', '-');
    return normalized === 'kr' || normalized.startsWith('ko')
        ? 'ko'
        : normalized.split('-')[0];
}

function resolveLocalizedDocument(source, language) {
    if (!isObject(source)) return null;
    if (isPrivacyDocument(source)) return source;

    const normalizedLanguage = normalizeLanguage(language);
    const languages = [
        normalizedLanguage,
        ...(normalizedLanguage === 'ko' ? ['kr'] : []),
        'ko',
        'kr',
        'en',
    ];
    const roots = [source, source.data, source.document, source.policy]
        .filter(isObject);

    for (const root of roots) {
        if (isPrivacyDocument(root)) return root;

        const containers = [
            root,
            root.languages,
            root.translations,
            root.locales,
            root.contents,
        ].filter(isObject);

        for (const container of containers) {
            for (const languageKey of languages) {
                if (isPrivacyDocument(container[languageKey])) {
                    return container[languageKey];
                }
            }
        }
    }

    return null;
}

function translateWithFallback(t, key, fallback) {
    return t(key, { defaultValue: fallback });
}

function joinItems(items) {
    return toArray(items).filter(
        (item) => item !== null && item !== undefined && String(item).trim(),
    ).join(', ');
}

function SectionBlock({ title, children, last = false }) {
    return (
        <section className={`privacy-consent-section${last ? ' is-last' : ''}`}>
            {title && (
                <h2 className="privacy-consent-section-title">{title}</h2>
            )}
            {children}
        </section>
    );
}

function ConsentList({ items }) {
    const normalizedItems = toArray(items).filter(Boolean);
    if (normalizedItems.length === 0) return null;

    return (
        <ul className="privacy-consent-list">
            {normalizedItems.map((item, index) => (
                <li key={`${String(item)}-${index}`}>{item}</li>
            ))}
        </ul>
    );
}

function Paragraphs({ items }) {
    return toArray(items).filter(Boolean).map((item, index) => (
        <p
            className="privacy-consent-text has-bottom-margin"
            key={`${String(item)}-${index}`}
        >
            {item}
        </p>
    ));
}

function HighlightedItems({ items }) {
    const normalizedItems = toArray(items).filter(Boolean);

    return normalizedItems.map((item, index) => (
        <React.Fragment key={`${String(item)}-${index}`}>
            <strong className="privacy-consent-highlight"><u>{item}</u></strong>
            {index < normalizedItems.length - 1 ? ', ' : ''}
        </React.Fragment>
    ));
}

function InformationTable({ rows }) {
    const normalizedRows = toArray(rows).filter(
        (row) => row && (row.label || row.value),
    );
    if (normalizedRows.length === 0) return null;

    return (
        <div className="privacy-consent-table-wrapper">
            <table className="privacy-consent-information-table">
                <tbody>
                    {normalizedRows.map((row, index) => (
                        <tr key={`${String(row.label)}-${index}`}>
                            <th scope="row">{row.label}</th>
                            <td>{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ProcessingTable({
    rows,
    purposeLabel,
    itemsLabel,
    retentionPeriodLabel,
}) {
    const normalizedRows = toArray(rows).filter(isObject);
    if (normalizedRows.length === 0) return null;

    return (
        <div className="privacy-consent-table-wrapper">
            <table className="privacy-consent-processing-table">
                <thead>
                    <tr>
                        <th>{purposeLabel}</th>
                        <th>{itemsLabel}</th>
                        <th>{retentionPeriodLabel}</th>
                    </tr>
                </thead>
                <tbody>
                    {normalizedRows.map((row, index) => (
                        <tr key={`${String(row.purpose)}-${index}`}>
                            <th scope="row">{row.purpose || '-'}</th>
                            <td>
                                {index === 0
                                    ? <HighlightedItems items={row.items} />
                                    : joinItems(row.items)}
                            </td>
                            <td>
                                <strong className="privacy-consent-highlight privacy-consent-retention-highlight">
                                    {row.retention_period || '-'}
                                </strong>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ContactTable({
    contacts,
    firstHeader,
    departmentLabel,
    nameLabel,
    contactLabel,
}) {
    const normalizedContacts = toArray(contacts).filter(isObject);
    if (normalizedContacts.length === 0) return null;

    return (
        <div className="privacy-consent-table-wrapper">
            <table className="privacy-consent-contact-table">
                <thead>
                    <tr>
                        <th className="privacy-consent-contact-label-cell">
                            {firstHeader}
                        </th>
                        {normalizedContacts.map((contact, index) => (
                            <th key={`contact-header-${index}`}>
                                {contact.type || contact.role ||
                                    contact.category || '-'}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <th>{departmentLabel}</th>
                        {normalizedContacts.map((contact, index) => (
                            <td key={`contact-department-${index}`}>
                                {contact.department || '-'}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <th>{nameLabel}</th>
                        {normalizedContacts.map((contact, index) => (
                            <td
                                className="privacy-consent-contact-name"
                                key={`contact-name-${index}`}
                            >
                                {contact.name || '-'}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <th>{contactLabel}</th>
                        {normalizedContacts.map((contact, index) => (
                            <td
                                className="privacy-consent-contact-value"
                                key={`contact-value-${index}`}
                            >
                                <span>T : {contact.phone || '-'}</span>
                                <span>E-mail : {contact.email || '-'}</span>
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function formatAgencies(agencies) {
    return toArray(agencies).filter(isObject).map((agency) => {
        const details = [
            agency.url ? `(${agency.url})` : '',
            agency.phone ? `T. ${agency.phone}` : '',
        ].filter(Boolean).join(' / ');

        return [agency.agency_name, details].filter(Boolean).join(' : ');
    });
}

export default function PrivacyConsentDocument({ documentData }) {
    const { t, i18n } = useTranslation('app');
    const currentLanguage = i18n.resolvedLanguage || i18n.language || 'ko';
    const resolvedDocument = useMemo(
        () => resolveLocalizedDocument(documentData, currentLanguage),
        [currentLanguage, documentData],
    );

    if (!resolvedDocument) {
        return (
            <div className="privacy-consent-empty">
                {translateWithFallback(
                    t,
                    'privacyPolicy.empty'
                )}
            </div>
        );
    }

    const documentInfo = resolvedDocument.document_info || {};
    const chapterMap = new Map(
        toArray(resolvedDocument.privacy_policy_chapters).map((chapter) => [
            Number(chapter.chapter),
            chapter,
        ]),
    );
    const chapters = Array.from(
        { length: 10 },
        (_, index) => chapterMap.get(index + 1),
    );
    const [
        chapter1,
        chapter2,
        chapter3,
        chapter4,
        chapter5,
        chapter6,
        chapter7,
        chapter8,
        chapter9,
        chapter10,
    ] = chapters;

    const contactLabels = {
        departmentLabel: translateWithFallback(
            t, 'privacyPolicy.document.department'
        ),
        nameLabel: translateWithFallback(
            t, 'privacyPolicy.document.name'
        ),
        contactLabel: translateWithFallback(
            t, 'privacyPolicy.document.contact'
        ),
    };
    const processingTableLabels = {
        purposeLabel: translateWithFallback(
            t, 'privacyPolicy.document.processingPurpose'
        ),
        itemsLabel: translateWithFallback(
            t, 'privacyPolicy.document.personalInformationItems',
            '개인정보 항목',
        ),
        retentionPeriodLabel: translateWithFallback(
            t, 'privacyPolicy.document.retentionPeriod'
        ),
    };
    const categoryLabel = translateWithFallback(
        t, 'privacyPolicy.document.category'
    );
    const personInChargeLabel = translateWithFallback(
        t, 'privacyPolicy.document.personInCharge'
    );
    const destructionRows = toArray(chapter5?.destruction_methods)
        .filter(isObject)
        .map((item) => ({ label: item.type, value: item.method }));

    return (
        <article className="privacy-consent-document">
            <header className="privacy-consent-document-header">
                {documentInfo.service_name && (
                    <span className="privacy-consent-service-badge">
                        {documentInfo.service_name}
                    </span>
                )}
                <h1>
                    {documentInfo.title || translateWithFallback(
                        t, 'privacyPolicy.title'
                    )}
                </h1>
                {documentInfo.company_name && (
                    <p className="privacy-consent-company">
                        {documentInfo.company_name}
                    </p>
                )}
                <p className="privacy-consent-version">
                    {documentInfo.version && <>Ver {documentInfo.version}</>}
                    {documentInfo.version && documentInfo.effective_date &&
                        <> · </>}
                    {documentInfo.effective_date && (
                        <>
                            {documentInfo.effective_date}{' '}
                            {translateWithFallback(
                                t, 'privacyPolicy.document.effective'
                            )}
                        </>
                    )}
                </p>
            </header>

            {chapter1 && (
                <SectionBlock title={chapter1.title}>
                    {chapter1.intro && (
                        <p className="privacy-consent-text has-bottom-margin">
                            {chapter1.intro}
                        </p>
                    )}
                    <ConsentList items={chapter1.items} />
                    {chapter1.note && (
                        <div className="privacy-consent-note">{chapter1.note}</div>
                    )}
                </SectionBlock>
            )}
            {chapter2 && (
                <SectionBlock title={chapter2.title}>
                    <Paragraphs items={chapter2.paragraphs} />
                </SectionBlock>
            )}
            {chapter3 && (
                <SectionBlock title={chapter3.title}>
                    <ProcessingTable
                        rows={chapter3.processing_table}
                        {...processingTableLabels}
                    />
                    {chapter3.note && (
                        <div className="privacy-consent-note">{chapter3.note}</div>
                    )}
                </SectionBlock>
            )}
            {chapter4 && (
                <SectionBlock title={chapter4.title}>
                    <Paragraphs items={chapter4.paragraphs} />
                </SectionBlock>
            )}
            {chapter5 && (
                <SectionBlock title={chapter5.title}>
                    {chapter5.content && (
                        <p className="privacy-consent-text has-bottom-margin">
                            {chapter5.content}
                        </p>
                    )}
                    <InformationTable rows={destructionRows} />
                </SectionBlock>
            )}
            {chapter6 && (
                <SectionBlock title={chapter6.title}>
                    <Paragraphs items={chapter6.paragraphs} />
                    <ContactTable
                        contacts={chapter6.contacts}
                        firstHeader={categoryLabel}
                        {...contactLabels}
                    />
                </SectionBlock>
            )}
            {chapter7 && (
                <SectionBlock title={chapter7.title}>
                    {chapter7.intro && (
                        <p className="privacy-consent-text has-bottom-margin">
                            {chapter7.intro}
                        </p>
                    )}
                    <ContactTable
                        contacts={chapter7.privacy_managers}
                        firstHeader={personInChargeLabel}
                        {...contactLabels}
                    />
                    {chapter7.external_remedy_agencies_intro && (
                        <p className="privacy-consent-text has-bottom-margin">
                            {chapter7.external_remedy_agencies_intro}
                        </p>
                    )}
                    <ConsentList items={formatAgencies(
                        chapter7.external_remedy_agencies,
                    )} />
                </SectionBlock>
            )}
            {chapter8 && (
                <SectionBlock title={chapter8.title}>
                    {chapter8.intro && (
                        <p className="privacy-consent-text has-bottom-margin">
                            {chapter8.intro}
                        </p>
                    )}
                    <ConsentList items={chapter8.measures} />
                </SectionBlock>
            )}
            {chapter9 && (
                <SectionBlock title={chapter9.title}>
                    <Paragraphs items={chapter9.paragraphs} />
                </SectionBlock>
            )}
            {chapter10 && (
                <SectionBlock
                    title={chapter10.title}
                    last={!resolvedDocument.supplementary_provision}
                >
                    {chapter10.content && (
                        <p className="privacy-consent-text">{chapter10.content}</p>
                    )}
                </SectionBlock>
            )}
            {resolvedDocument.supplementary_provision && (
                <SectionBlock last>
                    <p className="privacy-consent-text privacy-consent-supplementary">
                        {resolvedDocument.supplementary_provision}
                    </p>
                </SectionBlock>
            )}
        </article>
    );
}
